import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GlobalSearch from 'shared/components/inputs/GlobalSearch';
import {
  buttonClasses,
  chipClasses,
  buttonRowClasses,
} from 'styles/tailwindClassnames';
import SelectInput from 'shared/components/inputs/SelectInput';
import { routeDisplayName } from 'routes/routeDisplayName';
import { Icon } from 'react-icons-kit';
import { ic_close } from 'react-icons-kit/md/ic_close';

// A route RECORD from /v2/routes/: its own name and destinations, with the
// provider as an integration summary under data_providers. `provider` is kept for
// the older connection-shaped callers.
type RouteCandidate = {
  id: string;
  name?: string;
  provider?: { name?: string; type?: { name?: string } };
  data_providers?: {
    id?: string;
    name?: string;
    owner?: { name?: string };
    type?: { name?: string };
  }[];
  owner?: { id: string; name: string } | string;
  destinations?: { id: string }[];
};
type FilterOption = { label: string; value: string };

// A record's `owner` can come back as an id string; only an object carries a name.
const ownerName = (c: RouteCandidate) =>
  typeof c.owner === 'object' && c.owner ? c.owner.name : undefined;

type AddToRouteDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  candidates: RouteCandidate[];
  loading: boolean;
  // Attach this record to the chosen Route; the parent owns the PATCH.
  onAttach: (route: RouteCandidate) => Promise<void> | void;
  onSearch: (searchStr: string) => void;
  hasMore: boolean;
  workspaceOptions: FilterOption[];
  workspaceFilter: FilterOption | null;
  onChangeWorkspaceFilter: (option: FilterOption | null) => void;
  loadingWorkspaces?: boolean;
};

// Mirror of the flow map's AddDestinationDrawer, pointed the other way: instead of
// picking destinations for a Route, pick a Route for this record. Radio
// selection — a record joins one Route per confirm.
const AddToRouteDrawer = ({
  isOpen,
  onClose,
  candidates,
  loading,
  onAttach,
  onSearch,
  hasMore,
  workspaceOptions,
  workspaceFilter,
  onChangeWorkspaceFilter,
  loadingWorkspaces,
}: AddToRouteDrawerProps) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setSaving(false);
      setQuery('');
    }
  }, [isOpen]);

  const selected = candidates.find((c) => c.id === selectedId);

  return (
    <div
      data-testid="AddToRouteDrawer-panel"
      className={`fixed right-0 top-0 z-[60] flex h-full w-full flex-col border-l border-ui-divider-lines bg-white shadow-xl transition-transform duration-300 md:w-[420px] ${
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
      aria-hidden={!isOpen}
      {...({ inert: isOpen ? undefined : '' } as any)}
    >
      <div className="flex items-center justify-between border-b border-ui-divider-lines px-4 py-4">
        <h2 className="text-body-lg font-bold text-ui-text-primary-text">
          {t('Add to a Route')}
        </h2>
        <button
          onClick={onClose}
          aria-label={t('Close')}
          className="flex h-9 w-9 items-center justify-center text-ui-text-secondary-text hover:text-ui-text-primary-text"
        >
          <Icon icon={ic_close} size={24} />
        </button>
      </div>

      <div className="flex flex-col gap-3 px-4 pt-4">
        {/* The shared search box, same as every list page: the term is
                    applied on Enter or the search button, not as you type. */}
        <GlobalSearch
          onChangeGlobalSearch={(value) => {
            setQuery(value);
            onSearch(value);
          }}
          savedString={query}
          placeholder={t('Search Routes')}
        />
        <div className="flex flex-col gap-1">
          {/* Not filterClasses.label: that is now the on-tint treatment, and this
                        drawer is white. */}
          <span className="text-body-sm font-normal text-ui-text-primary-text">
            {t('Workspace')}
          </span>
          <SelectInput
            name="drawer_route_workspace"
            options={workspaceOptions}
            value={workspaceFilter}
            onChange={onChangeWorkspaceFilter}
            isMulti={false}
            isClearable={true}
            isSearchable={true}
            isDisabled={false}
            isLoading={!!loadingWorkspaces}
            className=""
            variant="routeFilter"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="mb-2 h-10 w-full animate-pulse rounded bg-teal-t30"
            />
          ))
        ) : candidates.length === 0 ? (
          <p className="mb-4 text-body-sm text-ui-text-primary-text">
            {query
              ? t('No Routes match your search.')
              : workspaceFilter
                ? t('No eligible Routes in this workspace.')
                : t('No eligible Routes.')}
          </p>
        ) : (
          candidates.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-start gap-3 border-b border-ui-divider-lines py-2"
            >
              <input
                type="radio"
                name="target-route"
                className="mt-1 accent-teal-t100"
                checked={selectedId === c.id}
                onChange={() => setSelectedId(c.id)}
              />
              <span className="min-w-0 flex-1">
                {/* The Route's own name — the picker lists route
                                        records, and a renamed Route must read the same
                                        here as everywhere else. */}
                <span className="block truncate text-body-sm text-ui-text-primary-text">
                  {routeDisplayName(c) || c.name || c.id}
                </span>
                {(ownerName(c) || c.data_providers?.[0]?.owner?.name) && (
                  <span className="block truncate text-body-sm text-ui-text-primary-text">
                    {ownerName(c) || c.data_providers?.[0]?.owner?.name}
                  </span>
                )}
              </span>
              {(c.provider?.type?.name ||
                c.data_providers?.[0]?.type?.name) && (
                <span className={`flex-none ${chipClasses.tag}`}>
                  {c.provider?.type?.name || c.data_providers?.[0]?.type?.name}
                </span>
              )}
            </label>
          ))
        )}
        {!loading && hasMore && (
          <p className="mt-2 text-body-sm text-ui-text-primary-text">
            {t(
              'Showing the first matches only — refine your search to find more.',
            )}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 border-t border-ui-divider-lines px-4 py-3">
        <div className={buttonRowClasses.row}>
          <button
            className={buttonClasses.primaryAction}
            disabled={!selected || saving}
            onClick={async () => {
              if (!selected) return;
              setSaving(true);
              try {
                await onAttach(selected);
              } finally {
                setSaving(false);
              }
            }}
          >
            {t('Add to Route')}
          </button>
          <button className={buttonClasses.cancelAction} onClick={onClose}>
            {t('Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToRouteDrawer;
